import React, { FC, ReactElement, useMemo } from 'react';
import { useJoin } from '../../contexts/JoinContext';
import JoinOptionButtonGroup from './JoinOptionButtonGroup';
import JoinActionButton from './JoinActionButton';
import JoinRadioButtonFormGroup from './JoinRadioButtonFormGroup';
import { ACCOUNT_TYPE_SUCCESSFUL_USER, YES_NO_OPTIONS } from './constants';
import { DashlineSeparator, FlexContainer, SeparatorLine, VerticalSeparator } from './styledElements';
import JoinSkipButton from './JoinSkipButton';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { Account, AccountMaker } from '../../models/Account';
import { connect } from 'react-redux';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import { RootState } from '../../store';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';

interface JoinProfileInfoProps {
  account: Account;
}
const JoinProfileInfo: FC<JoinProfileInfoProps> = ({ account }): ReactElement => {
  const { isMobile } = useMediaBreakpoints();
  const {
    currentFormData,
    setCurrentFormData,
    nextStep,
    handleShowSkipModal,
    prevStep,
    stepsFormData,
    setSkippedStepCounter,
    currentStepIndex,
    isMandatoryField,
  } = useJoin();

  const isSuccessfulUser = useMemo(() => stepsFormData[0].account_type === ACCOUNT_TYPE_SUCCESSFUL_USER, [
    stepsFormData,
  ]);

  const allowSubmit = useMemo(() => {
    const requiredFields = isSuccessfulUser
      ? [currentFormData.children, currentFormData.smoking, currentFormData.drinking]
      : [currentFormData.children, currentFormData.smoking, currentFormData.drinking];

    const hasValue = isMandatoryField()
      ? requiredFields.every(field => field !== '' && field !== null && field !== undefined)
      : requiredFields.some(field => field !== '' && field !== null && field !== undefined);

    return Boolean(hasValue);
  }, [currentFormData]);

  const isFormCompleted = () => {
    const hasValue = (value: string | number | null): boolean => {
      if (typeof value === 'undefined' || value === null) {
        return false;
      }

      return value !== '';
    };

    if (!hasValue(currentFormData?.children)) {
      return false;
    }

    if (!hasValue(currentFormData?.smoking)) {
      return false;
    }

    if (!hasValue(currentFormData?.smoking)) {
      return false;
    }

    return true;
  };

  const handleMixpanelEvent = (result?: string) => {
    if (!account?.isEligibleForMixpanelEvents()) {
      return;
    }

    if (!result) {
      result = isFormCompleted() ? 'Completed' : 'Skipped';
    }

    mixpanelTrackEvent({
      eventName: EVENT_NAMES.PROFILE_DETAILS_STEP_2_COMPLETED,
      payload: {
        eventProperties: {
          Result: result,
        },
      },
    });
  };

  const handleNextStep = () => {
    nextStep();
    handleMixpanelEvent();
    setSkippedStepCounter(prev => ({ ...prev, [currentStepIndex]: false }));
  };

  return (
    <div>
      {!isMobile ? <DashlineSeparator /> : <div style={{ marginTop: 35 }} />}
      <JoinRadioButtonFormGroup
        customRadioListStyle={{ marginTop: 11 }}
        label="Do You Have Any Children?"
        isRequired={isMandatoryField()}
      >
        <JoinOptionButtonGroup
          name="children"
          lists={YES_NO_OPTIONS(false, true)}
          value={currentFormData.children}
          onChangeHandler={item => {
            if (currentFormData.children === item.value) {
              return setCurrentFormData({
                children: null,
              });
            }
            setCurrentFormData({ children: item.value });
          }}
        />
      </JoinRadioButtonFormGroup>
      <DashlineSeparator />
      <JoinRadioButtonFormGroup
        customRadioListStyle={{ marginTop: 11 }}
        label="Do You Smoke?"
        isRequired={isMandatoryField()}
      >
        <JoinOptionButtonGroup
          name="smoking"
          lists={YES_NO_OPTIONS(true, true)}
          value={currentFormData.smoking}
          onChangeHandler={item => {
            if (currentFormData.smoking === item.value) {
              return setCurrentFormData({
                smoking: null,
              });
            }
            setCurrentFormData({ smoking: item.value });
          }}
        />
      </JoinRadioButtonFormGroup>
      <DashlineSeparator />
      <JoinRadioButtonFormGroup
        customRadioListStyle={{ marginTop: 11 }}
        label="Do You Drink?"
        isRequired={isMandatoryField()}
      >
        <JoinOptionButtonGroup
          name="drinking"
          lists={YES_NO_OPTIONS(true, true)}
          value={currentFormData.drinking}
          onChangeHandler={item => {
            if (currentFormData.drinking === item.value) {
              return setCurrentFormData({
                drinking: null,
              });
            }
            setCurrentFormData({ drinking: item.value });
          }}
        />
      </JoinRadioButtonFormGroup>
      <DashlineSeparator />

      <div style={{ textAlign: 'center' }}>
        <JoinActionButton label="Next" onClick={handleNextStep} isDisabled={!allowSubmit} isDefault={true}/>
        <FlexContainer>
          <VerticalSeparator>
            <JoinSkipButton label="Back" onClick={() => prevStep()} />
            {!isMandatoryField() && (
              <>
                <SeparatorLine />
                <JoinSkipButton
                  label="Skip"
                  onClick={() =>
                    handleShowSkipModal(() => {
                      handleMixpanelEvent('Skipped');
                      nextStep();
                      setSkippedStepCounter(prev => ({ ...prev, [currentStepIndex]: true }));
                    })
                  }
                />
              </>
            )}
          </VerticalSeparator>
        </FlexContainer>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    account: AccountMaker.create(state.profile),
  };
};

export default connect(mapStateToProps, {})(JoinProfileInfo);
